// ─────────────────────────────────────────────────────────────────────────────
// launch-event.tsx — Studio (c02) corporate variant: STARTUP / PRODUCT LAUNCH
//
// Reference language: Stripe Sessions × Apple Keynote × hackathon. Black on
// black with neon-green type, JetBrains Mono everywhere, terminal-window cards.
// This is *not* a gala (that's c01 Forum). This is tech-launch energy:
// confident, hacker-warm, B2B but not stuffy.
//
// Owned-file rules: this variant must compile against VariantProps unchanged.
// We do not import anything outside the allowed surface.
//
// Signature element: the hero subtitle types out character-by-character at
// ~30ms with a blinking neon-green block cursor. Once the type completes the
// cursor *stays blinking* (terminal idling). `prefers-reduced-motion: reduce`
// shows the full text instantly with a non-blinking cursor.
//
// Sound: 4 triggers — RSVP tap, share button click, agenda hover tap, speaker
// hover tap. NO chime (too celebratory for B2B tech).
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import {
  GuestPhoto,
  CountdownSection,
  StorySection,
  GallerySection,
  ScheduleSection,
  MapSection,
  RsvpSection,
  ShareSection,
  GuestFooter,
} from "@/components/sections";
import { useSound } from "@/lib/sound";
import { useT } from "@/lib/i18n";
import type { VariantProps } from "./types";

// ─── Reduced-motion helper ──────────────────────────────────────────────────
function usePrefersReducedMotion(): boolean {
  const [reduce, setReduce] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduce(mq.matches);
    const handler = (e: MediaQueryListEvent) => setReduce(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return reduce;
}

// ─── IntersectionObserver-based reveal (snappy: 200ms, 4px) ─────────────────
function useReveal<T extends HTMLElement>(threshold = 0.15) {
  const ref = useRef<T | null>(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setShown(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setShown(true);
            io.disconnect();
          }
        }
      },
      { threshold, rootMargin: "0px 0px -40px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);
  return { ref, shown };
}

// ─── Terminal-typing animation (signature element) ───────────────────────────
// Types `full` character-by-character at `speedMs`. When done, sets `done`
// true. The caller renders a blinking block cursor *next to* the text — the
// cursor keeps blinking even after typing completes (idle terminal feel).
function useTypewriter(full: string, speedMs = 30, enabled = true) {
  const [out, setOut] = useState(enabled ? "" : full);
  const [done, setDone] = useState(!enabled);
  useEffect(() => {
    if (!enabled) {
      setOut(full);
      setDone(true);
      return;
    }
    setOut("");
    setDone(false);
    let i = 0;
    const id = window.setInterval(() => {
      i += 1;
      setOut(full.slice(0, i));
      if (i >= full.length) {
        window.clearInterval(id);
        setDone(true);
      }
    }, speedMs);
    return () => window.clearInterval(id);
  }, [full, speedMs, enabled]);
  return { out, done };
}

// ─── Reveal wrapper: 200ms, 4px translate, opacity ──────────────────────────
function Reveal({
  children,
  reduce,
  delay = 0,
  as: Tag = "div",
  style,
  className,
}: {
  children: React.ReactNode;
  reduce: boolean;
  delay?: number;
  as?: "div" | "section";
  style?: CSSProperties;
  className?: string;
}) {
  const { ref, shown } = useReveal<HTMLDivElement>();
  const visible = reduce || shown;
  const merged: CSSProperties = {
    ...style,
    opacity: visible ? 1 : 0,
    transform: visible ? "translateY(0)" : "translateY(4px)",
    transition: reduce
      ? "none"
      : `opacity 200ms ease ${delay}ms, transform 200ms ease ${delay}ms`,
    willChange: reduce ? undefined : "opacity, transform",
  };
  if (Tag === "section") {
    return (
      <section
        ref={ref as React.RefObject<HTMLElement> as React.RefObject<HTMLDivElement>}
        className={className}
        style={merged}
      >
        {children}
      </section>
    );
  }
  return (
    <div ref={ref} className={className} style={merged}>
      {children}
    </div>
  );
}

// ─── Section heading: monospace `// 0N — TITLE` (neon prefix) ───────────────
function SectionLabel({
  n,
  label,
  accent,
  ink,
  font,
}: {
  n: string;
  label: string;
  accent: string;
  ink: string;
  font: string;
}) {
  return (
    <div
      className="studio-section-label"
      style={{
        fontFamily: font,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.3em",
        color: ink,
        marginBottom: 32,
        display: "flex",
        alignItems: "center",
        gap: 12,
        fontFeatureSettings: '"tnum" 1, "lnum" 1',
      }}
    >
      <span style={{ color: accent }}>// {n}</span>
      <span aria-hidden style={{ width: 24, height: 1, background: accent }} />
      <span>{label}</span>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// LaunchEventVariant — Studio (c02) startup product launch
// ═══════════════════════════════════════════════════════════════════════════
export function LaunchEventVariant({ t, isDark, ctx }: VariantProps) {
  const tt = useT();
  const reduce = usePrefersReducedMotion();
  const { play } = useSound();

  // Hero typing line — we type the lead-in, then settle into the headline.
  const heroTypingFull = "> Initialising launch...";
  const { out: heroTyped, done: heroDone } = useTypewriter(heroTypingFull, 30, !reduce);

  const tnum: CSSProperties = { fontFeatureSettings: '"tnum" 1, "lnum" 1' };

  // Roadmap — 4 steps from launch through 1-year horizon.
  const roadmap: Array<{ idx: string; quarter: string; title: string }> = [
    { idx: "01", quarter: tt("variant.c02.roadmap.0.quarter"), title: tt("variant.c02.roadmap.0.title") },
    { idx: "02", quarter: tt("variant.c02.roadmap.1.quarter"), title: tt("variant.c02.roadmap.1.title") },
    { idx: "03", quarter: tt("variant.c02.roadmap.2.quarter"), title: tt("variant.c02.roadmap.2.title") },
    { idx: "04", quarter: tt("variant.c02.roadmap.3.quarter"), title: tt("variant.c02.roadmap.3.title") },
  ];

  // Speakers — terminal-window cards.
  const speakers: Array<{ name: string; role: string; file: string }> = [
    { name: "Jasur Rakhimov", role: "FOUNDER · CEO", file: "ceo.ts" },
    { name: "Dilnoza Toshpulatova", role: "CTO", file: "cto.ts" },
    { name: "Sardor Akhmedov", role: "HEAD OF PRODUCT", file: "product.ts" },
  ];

  return (
    <div className="studio-variant" style={{ background: t.bg, color: t.ink }}>
      {/* ───── Scoped keyframes & mobile refinements ─────────────────────── */}
      <style>{`
        @keyframes studio-caret {
          0%, 49%   { opacity: 1; }
          50%, 100% { opacity: 0; }
        }
        @keyframes studio-scan {
          0%   { transform: translateY(-30%); opacity: 0.0; }
          12%  { opacity: 0.4; }
          50%  { opacity: 0.4; }
          88%  { opacity: 0.0; }
          100% { transform: translateY(130%); opacity: 0; }
        }
        @media (prefers-reduced-motion: reduce) {
          .studio-caret, .studio-caret-static, .studio-scan {
            animation: none !important;
          }
        }
        .studio-variant button:focus-visible,
        .studio-variant a:focus-visible {
          outline: 2px solid ${t.accent};
          outline-offset: 2px;
        }
        @media (max-width: 768px) {
          .studio-section-label { font-size: 10px !important; }
          .studio-roadmap-row {
            grid-template-columns: 92px 1fr !important;
            gap: 14px !important;
          }
          .studio-speakers-grid { grid-template-columns: 1fr !important; gap: 24px !important; }
          .studio-hero { padding: 64px 18px 48px !important; min-height: auto !important; text-align: center !important; }
          .studio-hero-name {
            font-size: clamp(36px, 12vw, 72px) !important;
            letter-spacing: -0.02em !important;
          }
          .studio-scan { display: none !important; }
          .studio-variant button,
          .studio-variant a[role="button"] { min-height: 44px; }
          /* Speaker terminal file-path text has no word-break — a path like
             "~/speakers/product.ts" can overflow a narrow card. Add truncation
             so the terminal bar doesn't cause horizontal scroll. */
          .studio-speaker-card .studio-terminal-file {
            overflow: hidden !important;
            text-overflow: ellipsis !important;
            white-space: nowrap !important;
            max-width: 180px !important;
          }
        }
      `}</style>

      {/* ═══════════════════════════════════════════════════════════════════
          HERO — black backdrop, terminal typing, neon-green block cursor
          ═══════════════════════════════════════════════════════════════════ */}
      <section
        className="studio-hero"
        style={{
          position: "relative",
          minHeight: "100vh",
          padding: "clamp(80px, 12vw, 140px) clamp(24px, 5vw, 60px)",
          display: "grid",
          alignContent: "center",
          background: t.bg,
          color: t.ink,
          overflow: "hidden",
          borderBottom: `1px solid ${t.accent}33`,
        }}
      >
        {/* Subtle dot-grid backdrop */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            backgroundImage: `radial-gradient(circle, ${t.accent}1A 1px, transparent 1px)`,
            backgroundSize: "22px 22px",
            backgroundPosition: "0 0",
            maskImage: "radial-gradient(ellipse at 50% 40%, #000 0%, #000 60%, transparent 100%)",
            WebkitMaskImage: "radial-gradient(ellipse at 50% 40%, #000 0%, #000 60%, transparent 100%)",
          }}
        />
        {/* Subtle scan-line gradient */}
        <div
          aria-hidden
          className="studio-scan"
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            background: `linear-gradient(180deg, transparent 0%, ${t.accent}08 45%, ${t.accent}14 50%, ${t.accent}08 55%, transparent 100%)`,
            mixBlendMode: "screen",
            animation: reduce ? undefined : "studio-scan 14s linear infinite",
          }}
        />

        <div style={{ position: "relative", maxWidth: 1100, margin: "0 auto", width: "100%" }}>
          {/* Top terminal comment */}
          <div
            style={{
              fontFamily: t.font,
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: "0.18em",
              color: t.accent,
              marginBottom: 28,
              ...tnum,
            }}
          >
            {"/* product × launch */"}
          </div>

          {/* Typing line — lead-in */}
          <div
            aria-live="polite"
            style={{
              fontFamily: t.font,
              fontSize: "clamp(15px, 1.7vw, 19px)",
              fontWeight: 600,
              color: t.accent,
              marginBottom: 36,
              minHeight: "1.4em",
              ...tnum,
            }}
          >
            <span>{heroTyped}</span>
            {/* Blinking neon-green block cursor — see signature element. */}
            <span
              aria-hidden
              className="studio-caret"
              style={{
                display: "inline-block",
                width: "0.55em",
                height: "1em",
                background: t.accent,
                verticalAlign: "-0.12em",
                marginLeft: 2,
                animation: reduce
                  ? undefined
                  : `studio-caret 0.9s steps(1, end) infinite ${heroDone ? "0s" : "0.6s"}`,
                opacity: reduce ? 1 : undefined,
              }}
            />
          </div>

          {/* Headline — PRODUCT × LAUNCH (uppercase mono, big) */}
          <h1
            className="studio-hero-name"
            style={{
              fontFamily: t.font,
              fontSize: "clamp(56px, 9vw, 128px)",
              lineHeight: 0.95,
              fontWeight: 700,
              letterSpacing: "-0.025em",
              margin: 0,
              color: t.ink,
              textTransform: "uppercase",
              ...tnum,
            }}
          >
            {t.names}
          </h1>

          {/* Date strip — bracketed mono */}
          <div
            style={{
              marginTop: 36,
              display: "flex",
              alignItems: "center",
              gap: 20,
              flexWrap: "wrap",
              fontFamily: t.font,
              fontSize: 14,
              fontWeight: 600,
              letterSpacing: "0.22em",
              color: `${t.ink}cc`,
              ...tnum,
            }}
          >
            <span style={{ color: t.accent }}>[{t.date}]</span>
            <span style={{ width: 24, height: 1, background: `${t.accent}66` }} aria-hidden />
            <span>{t.venue.toUpperCase()}</span>
          </div>

          {/* Doors line */}
          <div
            style={{
              marginTop: 18,
              fontFamily: t.font,
              fontSize: 13,
              fontWeight: 500,
              letterSpacing: "0.16em",
              color: `${t.ink}99`,
              display: "flex",
              alignItems: "center",
              gap: 8,
              ...tnum,
            }}
          >
            <span style={{ color: t.accent }}>{">"}</span>
            <span>{tt("variant.c02.hero.doors")}</span>
          </div>
        </div>
      </section>

      {/* ───── // 01 — COUNTDOWN ────────────────────────────────────────── */}
      <Reveal reduce={reduce} as="div">
        <div style={{ borderTop: `1px solid ${t.accent}33` }}>
          <div style={{ padding: "clamp(56px, 8vw, 96px) clamp(24px, 5vw, 60px) 0" }}>
            <SectionLabel n="01" label="COUNTDOWN" accent={t.accent} ink={t.ink} font={t.font} />
          </div>
          <CountdownSection t={t} {...ctx.countdown} isDark={isDark} />
        </div>
      </Reveal>

      {/* ───── // 02 — ROADMAP (signature unique section) ──────────────── */}
      <Reveal
        reduce={reduce}
        as="section"
        style={{
          padding: "clamp(72px, 10vw, 120px) clamp(24px, 5vw, 60px)",
          borderTop: `1px solid ${t.accent}33`,
          background: t.bg,
        }}
      >
        <div style={{ maxWidth: 880, margin: "0 auto" }}>
          <SectionLabel n="02" label="ROADMAP" accent={t.accent} ink={t.ink} font={t.font} />
          <div style={{ marginBottom: 24 }}>
            <p
              style={{
                fontFamily: t.font,
                fontSize: "clamp(14px, 1.4vw, 16px)",
                lineHeight: 1.65,
                color: `${t.ink}aa`,
                margin: 0,
                maxWidth: "60ch",
                ...tnum,
              }}
            >
              {tt("variant.c02.roadmap.intro")}
            </p>
          </div>

          <div role="list" style={{ display: "grid", gap: 0 }}>
            {roadmap.map((r, i) => (
              <div
                key={r.idx}
                role="listitem"
                className="studio-roadmap-row"
                style={{
                  display: "grid",
                  gridTemplateColumns: "200px 1fr",
                  gap: 32,
                  alignItems: "center",
                  padding: "22px 0",
                  borderTop: i === 0 ? `1px solid ${t.accent}55` : `1px solid ${t.ink}22`,
                  borderBottom: i === roadmap.length - 1 ? `1px solid ${t.accent}55` : "none",
                  ...tnum,
                }}
              >
                <span
                  style={{
                    fontFamily: t.font,
                    fontSize: 13,
                    fontWeight: 700,
                    letterSpacing: "0.14em",
                    color: t.accent,
                  }}
                >
                  [{r.idx} — {r.quarter}]
                </span>
                <span
                  style={{
                    fontFamily: t.font,
                    fontSize: "clamp(15px, 1.6vw, 18px)",
                    fontWeight: 500,
                    color: t.ink,
                    letterSpacing: "0.01em",
                  }}
                >
                  {r.title}
                </span>
              </div>
            ))}
          </div>
        </div>
      </Reveal>

      {/* ───── // 03 — STORY (// about) ────────────────────────────────── */}
      <Reveal reduce={reduce} as="div">
        <div style={{ borderTop: `1px solid ${t.accent}33` }}>
          <div style={{ padding: "clamp(48px, 8vw, 96px) clamp(24px, 5vw, 60px) 0" }}>
            <SectionLabel n="03" label="ABOUT" accent={t.accent} ink={t.ink} font={t.font} />
          </div>
          <StorySection
            t={t}
            title={tt("variant.c02.story.title")}
            body={tt("variant.c02.story.body")}
          />
        </div>
      </Reveal>

      {/* ───── // 04 — GALLERY (neon frames + scanlines) ───────────────── */}
      <Reveal reduce={reduce} as="div">
        <div style={{ borderTop: `1px solid ${t.accent}33`, position: "relative" }}>
          <div style={{ padding: "clamp(48px, 8vw, 96px) clamp(24px, 5vw, 60px) 0" }}>
            <SectionLabel n="04" label="GALLERY" accent={t.accent} ink={t.ink} font={t.font} />
          </div>
          {/* Scoped style: neon-green frames + scanline overlay on gallery photos. */}
          <style>{`
            .studio-variant section[class*="cv-auto"] [style*="aspectRatio"] > div {
              position: relative;
            }
          `}</style>
          <div
            style={{
              // Tint the gallery section with neon hairlines on photos by overlaying.
              position: "relative",
            }}
          >
            <div
              aria-hidden
              style={{
                // Scanlines layered above the photos but below interactive controls.
                position: "absolute",
                inset: 0,
                pointerEvents: "none",
                background: `repeating-linear-gradient(0deg, transparent 0 4px, ${t.accent}08 4px 5px)`,
                opacity: 0.5,
                zIndex: 1,
              }}
            />
            <GallerySection t={t} />
          </div>
        </div>
      </Reveal>

      {/* ───── // 05 — SCHEDULE ────────────────────────────────────────── */}
      <Reveal reduce={reduce} as="div">
        <div
          style={{ borderTop: `1px solid ${t.accent}33` }}
          onMouseOverCapture={(e) => {
            const target = e.target as HTMLElement;
            if (target && target.tagName === "BUTTON") play("tap");
          }}
        >
          <div style={{ padding: "clamp(48px, 8vw, 96px) clamp(24px, 5vw, 60px) 0" }}>
            <SectionLabel n="05" label="SCHEDULE" accent={t.accent} ink={t.ink} font={t.font} />
          </div>
          <ScheduleSection
            t={t}
            items={[
              ["19:00", tt("variant.c02.schedule.0.event")],
              ["19:30", tt("variant.c02.schedule.1.event")],
              ["20:00", tt("variant.c02.schedule.2.event")],
              ["20:30", tt("variant.c02.schedule.3.event")],
              ["21:30", tt("variant.c02.schedule.4.event")],
              ["22:30", tt("variant.c02.schedule.5.event")],
            ]}
          />
        </div>
      </Reveal>

      {/* ───── // 06 — SPEAKERS (terminal-window cards) ────────────────── */}
      <Reveal
        reduce={reduce}
        as="section"
        style={{
          padding: "clamp(72px, 10vw, 120px) clamp(24px, 5vw, 60px)",
          borderTop: `1px solid ${t.accent}33`,
          background: `${t.accent}05`,
        }}
      >
        <SectionLabel n="06" label="SPEAKERS" accent={t.accent} ink={t.ink} font={t.font} />
        <div
          className="studio-speakers-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: 24,
          }}
        >
          {speakers.map((sp, i) => (
            <SpeakerTerminal
              key={sp.name}
              speaker={sp}
              photo={t.photos[i] || t.photos[0]}
              t={t}
              reduce={reduce}
              onHover={() => play("tap")}
            />
          ))}
        </div>
      </Reveal>

      {/* ───── // 07 — VENUE / MAP ─────────────────────────────────────── */}
      <Reveal reduce={reduce} as="div">
        <div style={{ borderTop: `1px solid ${t.accent}33` }}>
          <div style={{ padding: "clamp(48px, 8vw, 96px) clamp(24px, 5vw, 60px) 0" }}>
            <SectionLabel n="07" label="VENUE" accent={t.accent} ink={t.ink} font={t.font} />
          </div>
          <MapSection t={t} isDark={isDark} addressLine={tt("variant.c02.map.address")} />
        </div>
      </Reveal>

      {/* ───── // 08 — RSVP ────────────────────────────────────────────── */}
      <Reveal reduce={reduce} as="div">
        <div style={{ borderTop: `1px solid ${t.accent}33` }}>
          <div style={{ padding: "clamp(48px, 8vw, 96px) clamp(24px, 5vw, 60px) 0" }}>
            <SectionLabel n="08" label="RSVP" accent={t.accent} ink={t.ink} font={t.font} />
          </div>
          <div
            // RSVP tap sound
            onClickCapture={(e) => {
              const target = e.target as HTMLElement;
              if (target && target.tagName === "BUTTON") play("tap");
            }}
          >
            <RsvpSection t={t} isDark={isDark} {...ctx.rsvp} label="rsvp.confirm" />
          </div>
        </div>
      </Reveal>

      {/* ───── // 09 — SHARE ──────────────────────────────────────────── */}
      <Reveal reduce={reduce} as="div">
        <div style={{ borderTop: `1px solid ${t.accent}33` }}>
          <div style={{ padding: "clamp(48px, 8vw, 96px) clamp(24px, 5vw, 60px) 0" }}>
            <SectionLabel n="09" label="SHARE" accent={t.accent} ink={t.ink} font={t.font} />
          </div>
          <div
            onClickCapture={(e) => {
              const target = e.target as HTMLElement;
              if (target && target.closest("button")) play("click");
            }}
          >
            <ShareSection t={t} />
          </div>
        </div>
      </Reveal>

      {/* ───── FOOTER ──────────────────────────────────────────────────── */}
      <div style={{ borderTop: `1px solid ${t.accent}33` }}>
        <GuestFooter t={t} />
      </div>
    </div>
  );
}

// ─── SpeakerTerminal: terminal-window-styled speaker card ───────────────────
function SpeakerTerminal({
  speaker,
  photo,
  t,
  reduce,
  onHover,
}: {
  speaker: { name: string; role: string; file: string };
  photo: string | undefined;
  t: VariantProps["t"];
  reduce: boolean;
  onHover: () => void;
}) {
  const [hover, setHover] = useState(false);
  const tnum: CSSProperties = { fontFeatureSettings: '"tnum" 1, "lnum" 1' };
  return (
    <div
      className="studio-speaker-card"
      onMouseEnter={() => {
        setHover(true);
        onHover();
      }}
      onMouseLeave={() => setHover(false)}
      style={{
        border: `1px solid ${t.accent}`,
        background: t.bg,
        display: "flex",
        flexDirection: "column",
        transition: reduce ? "none" : "transform 200ms ease, box-shadow 200ms ease",
        transform: hover && !reduce ? "translateY(-2px)" : "translateY(0)",
        boxShadow: hover && !reduce ? `0 0 0 1px ${t.accent}, 0 8px 24px -8px ${t.accent}55` : "none",
      }}
    >
      {/* terminal title bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "10px 14px",
          borderBottom: `1px solid ${t.accent}55`,
          background: `${t.accent}0A`,
        }}
      >
        <span style={{ display: "inline-flex", gap: 6 }}>
          {[0, 1, 2].map((d) => (
            <span
              key={d}
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                border: `1px solid ${t.accent}`,
                background: "transparent",
              }}
            />
          ))}
        </span>
        <span
          className="studio-terminal-file"
          style={{
            fontFamily: t.font,
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: "0.12em",
            color: `${t.ink}aa`,
            ...tnum,
          }}
        >
          ~/speakers/{speaker.file}
        </span>
      </div>

      {/* preview pane: photo with neon-green tint + scanlines */}
      <div
        style={{
          aspectRatio: "1 / 1",
          background: `${t.ink}11`,
          overflow: "hidden",
          position: "relative",
          borderBottom: `1px solid ${t.accent}55`,
        }}
      >
        <GuestPhoto
          src={photo}
          style={{
            width: "100%",
            height: "100%",
            transition: reduce ? "none" : "filter 220ms ease",
          }}
          filter={
            hover && !reduce
              ? "grayscale(0.85) contrast(1.1) brightness(0.9) hue-rotate(70deg)"
              : "grayscale(0.95) contrast(1.05) brightness(0.85) hue-rotate(70deg)"
          }
          fallbackBg={t.bg}
          fallbackTone={t.accent}
        />
        {/* green tint */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            background: `linear-gradient(180deg, ${t.accent}14 0%, transparent 35%, transparent 65%, ${t.accent}1A 100%)`,
            pointerEvents: "none",
            mixBlendMode: "screen",
          }}
        />
        {/* scanline overlay */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            background: `repeating-linear-gradient(0deg, transparent 0 3px, ${t.accent}10 3px 4px)`,
            pointerEvents: "none",
            opacity: hover ? 0.95 : 0.7,
            transition: reduce ? "none" : "opacity 200ms ease",
          }}
        />
      </div>

      {/* "code" body */}
      <div style={{ padding: "16px 16px 18px" }}>
        <div
          style={{
            fontFamily: t.font,
            fontSize: 11,
            color: `${t.ink}88`,
            marginBottom: 6,
            ...tnum,
          }}
        >
          <span style={{ color: `${t.accent}aa` }}>const </span>
          <span style={{ fontWeight: 700, color: t.ink }}>speaker</span>
          <span> = {"{"}</span>
        </div>
        <div
          style={{
            fontFamily: t.font,
            fontSize: 16,
            fontWeight: 700,
            letterSpacing: "-0.01em",
            color: t.ink,
            paddingLeft: 16,
            marginBottom: 4,
          }}
        >
          <span style={{ color: `${t.accent}` }}>name</span>
          <span style={{ color: `${t.ink}88`, fontWeight: 500 }}>: </span>
          <span style={{ fontWeight: 500 }}>"{speaker.name}"</span>
          <span style={{ color: `${t.ink}88`, fontWeight: 500 }}>,</span>
        </div>
        <div
          style={{
            fontFamily: t.font,
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: "0.18em",
            paddingLeft: 16,
            color: t.ink,
            ...tnum,
          }}
        >
          <span style={{ color: t.accent }}>role</span>
          <span style={{ color: `${t.ink}88`, fontWeight: 500 }}>: </span>
          <span style={{ letterSpacing: "0.22em" }}>"{speaker.role}"</span>
        </div>
        <div
          style={{
            fontFamily: t.font,
            fontSize: 11,
            color: `${t.ink}88`,
            marginTop: 6,
            ...tnum,
          }}
        >
          {"}"};
        </div>
      </div>
    </div>
  );
}
